import { ReceiptPrintName } from '@/types/receipt'
import { exists } from '@/api'

export const CommandType = {
  ScreenShot: 'SCREENSHOT',
  ImeOn: 'IME_ON',
  ImeOff: 'IME_OFF',
  OpenExcel: 'OPEN_EXCEL',
  ReceiptPrintLend: 'RECEIPT_PRINT_LEND',
  ReceiptPrintLendStatus: 'RECEIPT_PRINT_LEND_STATUS',
  ReceiptPrintBibliography: 'RECEIPT_PRINT_BIBLIOGRAPHY',
  ReceiptPrintReserve: 'RECEIPT_PRINT_RESERVE',
  ReceiptPrintReserveForwarding: 'RECEIPT_PRINT_RESERVE_FORWARDING',
  ReceiptPrintReserveStatus: 'RECEIPT_PRINT_RESERVE_STATUS',
  ReceiptPrintShelf: 'RECEIPT_PRINT_SHELF',
  ReceiptPrintLost: 'RECEIPT_PRINT_LOST',
  ReceiptPrintReturnConfirm: 'RECEIPT_PRINT_RETURN_CONFIRM',
  ReceiptPrintInterlibraryLoanBorrowReturnConfirm:
    'RECEIPT_PRINT_INTERLIBRARY_LOAN_BORROW_RETURN_CONFIRM',
  ReceiptPrintMemo: 'RECEIPT_PRINT_MEMO',
  ReceiptPrintAddressee: 'RECEIPT_PRINT_ADDRESSEE',
  ReceiptPrintUserDueDateNotice: 'RECEIPT_PRINT_USER_DUE_DATE_NOTICE',
  ReceiptPrintReceiveWaitingUser: 'RECEIPT_PRINT_RECEIVE_WAITING_USER',
  ReceiptPrintCollectionStatusConfirm:
    'RECEIPT_PRINT_COLLECTION_STATUS_CONFIRM',
  OpenHandyTerminal: 'OPEN_HANDY_TERMINAL',
  ICTagReading: 'IC_TAG_READING',
  ICTagWriting: 'IC_TAG_WRITING',
  AFIWriting: 'IC_TAG_AFI_WRITING',
  AutoLib: 'AUTO_LIB',
  ReWriteCardRead: 'REWRITECARD_READ',
  ReWriteCardReadComplete: 'REWRITECARD_READ_COMPLETE',
  ReWriteCardReadCancel: 'REWRITECARD_READ_CANCEL',
  ReWriteCardWrite: 'REWRITECARD_WRITE',
  ReWriteCardWriteComplete: 'REWRITECARD_WRITE_COMPLETE',
  ReWriteCardEject: 'REWRITECARD_EJECT',
  ReWriteCardErase: 'REWRITECARD_ERASE',
  FelicaRead: 'FELICA_READ',
  FelicaReadCancel: 'FELICA_READ_CANCEL',
  FelicaReadComplete: 'FELICA_READ_COMPLETE',
  Connect: 'CONNECT'
} as const
export type CommandType = typeof CommandType[keyof typeof CommandType]

export const ErrorCode = {
  ReWriteCard: {
    ReWriteCardUserNoNotWritten: 'REWRITECARD_USER_NO_NOT_WRITTEN', // 利用者番号が書込まれていません。
    RewriteCardRequestFailed: 'REWRITECARD_REQUEST_FAILED', // 利用者カード読込に失敗しました。
    RewriteCardClosed: 'REWRITECARD_CLOSED' // リライトカード機器の電源が入っていないか接続されていません。
  }
}

export interface ConnectParameter {
  command: string
  result: boolean
  code: string
  message: string
  data: ConnectParameterData
}

export interface ConnectParameterData {
  authKey: string
  ictagEnabled: boolean
  ictagVendor: string
  rewritecardEnabled: boolean
}

export const defaultConnectParameter = (): ConnectParameter => ({
  command: '',
  result: false,
  code: '',
  message: '',
  data: defaultConnectParameterData()
})

export const defaultConnectParameterData = (): ConnectParameterData => ({
  authKey: '',
  ictagEnabled: false,
  ictagVendor: '',
  rewritecardEnabled: false
})

export interface ScreenshotParameter {
  x: number
  y: number
  width: number
  height: number
}

export interface ScreenshotCommand extends ScreenshotParameter {
  command: CommandType
}

export interface ImeOnCommand {
  command: CommandType
  data: ImeOnCommandData
}

export interface ImeOnCommandData {
  kana: boolean
}

export interface ImeOffCommand {
  command: CommandType
}

export interface OpenExcelParameter {
  formatType: string
  csvUrl: string
  templateUrl: string
}

export interface OpenExcelCommand extends OpenExcelParameter {
  command: CommandType
}

export interface ReceiptPrintParameter {
  command: CommandType
  name?: ReceiptPrintName
  data: any // このプロパティのオブジェクトプロパティ設定はスネークケース前提なので注意
  subject?: string
  maxPrintCount?: number
  customerCode?: string
}

export interface OpenHandyTerminalCommand {
  command: CommandType
}

export interface SendIcTagReadingCommand {
  command: CommandType
  agencyCode?: string
}

export interface SendIcTagWritingCommand {
  command: CommandType
  name: string
  data: SendIcTagWritingCommandData
}

export interface SendIcTagWritingCommandData {
  industryCode: string
  branchType: string
  agencyCode: string
  noFlag: string
  no: string
}

export interface SendAFIWritingCommand {
  command: CommandType
  name: string
  data: SendAFIWritingCommandData
}

export interface SendAFIWritingCommandData {
  afi: string
  collectionNos: string[]
  agencyCode?: string // クライアントサービス側で機関コードチェックを行う時だけ設定する
  continuousReading?: string
}

export interface IcTagReadingParameter {
  command: string
  message: string
  code: string
  result: boolean
  data: IcTagReadingParameterData[]
}

export interface IcTagReadingParameterData {
  tagNo: string
  industryCode: string
  branchType: string
  agencyCode: string
  noFlag: string
  no: string
  afi: string
  oldTag: string
}

export interface AFIWritingParameter {
  command: string
  message: string
  code: string
  result: boolean
  data: AFIWritingParameterData[]
  readTagCount: number
  writeTagCount: number
}

export interface AFIWritingParameterData {
  industryCode: string
  branchType: string
  agencyCode: string
  noFlag: string
  no: string
  afi: string
}

export interface IcTagWritingParameter {
  command: string
  message: string
  code: string
  result: boolean
  data: IcTagWritingParameterData[]
}

export interface IcTagWritingParameterData {
  industryCode: string
  branchType: string
  agencyCode: string
  noFlag: string
  no: string
  afi: string
}

export interface SendAutoLibCommandData {
  collectionNo: string
  retrievalStationCode: string
}

export interface SendAutoLibCommand {
  command: CommandType
  name: string
  data: SendAutoLibCommandData
}

export const IcTagWriteIncompatible = 'IC_TAG_WRITE_INCOMPATIBLE'

export const IcTagNotFound = 'IC_TAG_NOT_FOUND'

export function ConnectParameterFromJSON(json: any): ConnectParameter {
  return {
    command: exists(json, 'command') ? json.command : '',
    result: exists(json, 'result') ? json.result : false,
    code: exists(json, 'code') ? json.code : '',
    message: exists(json, 'message') ? json.message : '',
    data: exists(json, 'data')
      ? ConnectParameterDataFromJSON(json.data)
      : defaultConnectParameterData()
  }
}

export function ConnectParameterDataFromJSON(json: any): ConnectParameterData {
  return {
    authKey: exists(json, 'auth_key') ? json.auth_key : '',
    ictagEnabled: exists(json, 'ictag_enabled') ? json.ictag_enabled : false,
    ictagVendor: exists(json, 'ictag_vendor') ? json.ictag_vendor : '',
    rewritecardEnabled: exists(json, 'rewritecard_enabled')
      ? json.rewritecard_enabled
      : false
  }
}

export function SendImeOnCommandToJSON(value: ImeOnCommand): any {
  return {
    command: value.command,
    data: value.data ? SendImeOnCommandDataToJSON(value.data) : undefined
  }
}

export function SendImeOnCommandDataToJSON(value: ImeOnCommandData): any {
  return {
    kana: value.kana
  }
}

export function SendIcTagReadingCommandToJSON(
  value: SendIcTagReadingCommand
): any {
  return {
    command: value.command,
    agency_code: value.agencyCode !== '' ? value.agencyCode : undefined
  }
}

export function SendIcTagWritingCommandToJSON(
  value: SendIcTagWritingCommand
): any {
  return {
    command: value.command,
    data: value.data ? SendIcTagWritingCommandDataToJSON(value.data) : undefined
  }
}

export function SendIcTagWritingCommandDataToJSON(
  value: SendIcTagWritingCommandData
): any {
  return {
    industry_code: value.industryCode !== '' ? value.industryCode : undefined,
    branch_type: value.branchType !== '' ? value.branchType : undefined,
    agency_code: value.agencyCode !== '' ? value.agencyCode : undefined,
    no_flag: value.noFlag !== '' ? value.noFlag : undefined,
    no: value.no !== '' ? value.no : undefined
  }
}

export function IcTagWritingParameterFromJSON(
  json: any
): IcTagWritingParameter {
  return {
    command: exists(json, 'command') ? json.command : '',
    message: exists(json, 'message') ? json.message : '',
    code: exists(json, 'code') ? json.code : '',
    result: exists(json, 'result') ? json.result : false,
    data: exists(json, 'data')
      ? json.data.map(IcTagWritingParameterDataFromJSON)
      : []
  }
}

export function IcTagWritingParameterDataFromJSON(
  json: any
): IcTagWritingParameterData {
  return {
    industryCode: exists(json, 'industry_code') ? json.industry_code : '',
    branchType: exists(json, 'branch_type') ? json.branch_type : '',
    agencyCode: exists(json, 'agency_code') ? json.agency_code : '',
    noFlag: exists(json, 'no_flag') ? json.no_flag : '',
    no: exists(json, 'no') ? json.no : '',
    afi: exists(json, 'afi') ? json.afi : ''
  }
}

export function IcTagReadingParameterFromJSON(
  json: any
): IcTagReadingParameter {
  return {
    command: exists(json, 'command') ? json.command : '',
    message: exists(json, 'message') ? json.message : '',
    code: exists(json, 'code') ? json.code : '',
    result: exists(json, 'result') ? json.result : false,
    data: exists(json, 'data')
      ? json.data.map(IcTagReadingParameterDataFromJSON)
      : []
  }
}

export function IcTagReadingParameterDataFromJSON(
  json: any
): IcTagReadingParameterData {
  return {
    tagNo: exists(json, 'tag_no') ? json.tag_no : '',
    industryCode: exists(json, 'industry_code') ? json.industry_code : '',
    branchType: exists(json, 'branch_type') ? json.branch_type : '',
    agencyCode: exists(json, 'agency_code') ? json.agency_code : '',
    noFlag: exists(json, 'no_flag') ? json.no_flag : '',
    no: exists(json, 'no') ? json.no : '',
    afi: exists(json, 'afi') ? json.afi : '',
    oldTag: exists(json, 'old_tag') ? json.old_tag : ''
  }
}

export function SendAFIWritingCommandToJSON(value: SendAFIWritingCommand): any {
  return {
    command: value.command,
    name: value.name !== '' ? value.name : undefined,
    data: value.data ? SendAFIWritingCommandDataToJSON(value.data) : undefined
  }
}

export function SendAFIWritingCommandDataToJSON(
  value: SendAFIWritingCommandData
): any {
  return {
    afi: value.afi,
    collection_nos: value.collectionNos,
    agency_code: value.agencyCode ?? '',
    continuous_reading: value.continuousReading ?? '0'
  }
}

export function AFIWritingParameterFromJSON(json: any): AFIWritingParameter {
  return {
    command: exists(json, 'command') ? json.command : '',
    message: exists(json, 'message') ? json.message : '',
    code: exists(json, 'code') ? json.code : '',
    result: exists(json, 'result') ? json.result : false,
    data: exists(json, 'data')
      ? json.data.map(AFIWritingParameterDataFromJSON)
      : [],
    readTagCount: exists(json, 'read_tag_count') ? json.read_tag_count : 0,
    writeTagCount: exists(json, 'write_tag_count') ? json.write_tag_count : 0
  }
}

export function AFIWritingParameterDataFromJSON(
  json: any
): AFIWritingParameterData {
  return {
    industryCode: exists(json, 'industry_code') ? json.industry_code : '',
    branchType: exists(json, 'branch_type') ? json.branch_type : '',
    agencyCode: exists(json, 'agency_code') ? json.agency_code : '',
    noFlag: exists(json, 'no_flag') ? json.no_flag : '',
    no: exists(json, 'no') ? json.no : '',
    afi: exists(json, 'afi') ? json.afi : ''
  }
}

export function SendAutoLibCommandToJSON(value: SendAutoLibCommand): any {
  return {
    command: value.command,
    name: value.name,
    data: {
      collection_no: value.data.collectionNo,
      retrieval_station_code: value.data.retrievalStationCode
    }
  }
}

export function ReceiptPrintParameterToJSON(value: ReceiptPrintParameter): any {
  return {
    command: value.command ?? undefined,
    name: value?.name ?? undefined,
    data: value.data ?? undefined,
    subject: value?.subject || undefined,
    max_print_count: value?.maxPrintCount || undefined,
    customer_code: value?.customerCode || undefined
  }
}

// --------------------
// リライトカード
// --------------------

// 読込
export interface ReWriteCardReadCommand {
  command: CommandType
  name: string
}

// 読込キャンセル
export interface ReWriteCardReadCancelCommand {
  command: CommandType
  name: string
}

// 書込
export interface ReWriteCardWriteCommand {
  command: CommandType
  name: string
  data: SendReWriteCardWriteCommandData
}

export interface SendReWriteCardWriteCommandData {
  userNo: string
  branchName: string
  collections: SendReWriteCardWriteCommandCollectionData[]
  writeFlag: string
}

export interface SendReWriteCardWriteCommandCollectionData {
  title: string
  dueDate: string
}

export function SendReWriteCardWriteCommandToJSON(
  value: ReWriteCardWriteCommand
): any {
  return {
    command: value.command,
    name: value.name,
    data: value.data
      ? SendReWriteCardWriteCommandDataToJSON(value.data)
      : undefined
  }
}

export function SendReWriteCardWriteCommandDataToJSON(
  value: SendReWriteCardWriteCommandData
): any {
  return {
    user_no: value.userNo,
    branch_name: value.branchName !== '' ? value.branchName : undefined,
    collections: value.collections.map(
      SendReWriteCardWriteCommandCollectionDataToJSON
    ),
    write_flag: value.writeFlag !== '' ? value.writeFlag : undefined
  }
}

export function SendReWriteCardWriteCommandCollectionDataToJSON(
  value: SendReWriteCardWriteCommandCollectionData
): any {
  return {
    title: value.title !== '' ? value.title : undefined,
    due_date: value.dueDate !== '' ? value.dueDate : undefined
  }
}

// 排出
export interface ReWriteCardEjectCommand {
  command: CommandType
  name: string
}

// 消去
export interface ReWriteCardEraseCommand {
  command: CommandType
  name: string
}

// レスポンス
export interface ReWriteCardParameter {
  command: string
  result: boolean
  message: string
  code: string
  userNo: string
  rewritecardEnabled: boolean // 設定自体はあるので一応用意しているが、ここの処理は接続チェック段階で行っている
}

export function ReWriteCardParameterFromJSON(json: any): ReWriteCardParameter {
  return {
    command: exists(json, 'command') ? json.command : '',
    result: exists(json, 'result') ? json.result : false,
    message: exists(json, 'message') ? json.message : '',
    code: exists(json, 'code') ? json.code : '',
    userNo: exists(json, 'user_no') ? json.user_no : '',
    rewritecardEnabled: exists(json, 'rewritecard_enabled')
      ? json.rewritecard_enabled
      : false
  }
}

// --------------------
// Felica
// --------------------

// 読込
export interface FelicaReadCommand {
  command: CommandType
  name: string
}

// 読込キャンセル
export interface FelicaReadCancelCommand {
  command: CommandType
  name: string
}

// レスポンス
export interface FelicaParameter {
  command: string
  result: boolean
  message: string
  code: string
  id: string
}

export function FelicaParameterFromJSON(json: any): FelicaParameter {
  return {
    command: exists(json, 'command') ? json.command : '',
    result: exists(json, 'result') ? json.result : false,
    message: exists(json, 'message') ? json.message : '',
    code: exists(json, 'code') ? json.code : '',
    id: exists(json, 'id') ? json.id : ''
  }
}
